import React from 'react'
import { CurrencyField, FundDistribution, Segment, Select, TextField, TextInput } from '../../../../../views/components'
import styles from './PaymentAllocation.module.scss'
import { subscribe } from 'react-contextual'
import AdhocInvoiceProvider from '../../../AdhocInvoiceProvider'
import { useSelector } from 'react-redux'
import { depositInvoiceTypes } from 'modules/api/invoice/constants'
import { map, pathOr, pipe, sum, map as rMap } from 'ramda'
import { $TSFixMe } from 'types/ts-migrate'
import { agencyApiSelectors } from 'modules/api/agency'
import { TextFieldTypes } from 'views/components/atoms/TextField/text-field.types'
import { portfolioApiSelectors } from 'modules/api/portfolio'
import { partyApiSelectors } from 'modules/api/party'
import FundDistributionFooter from 'views/components/organisms/FormFragments/FundDistribution/FundDistributionFooter/FundDistributionFooter'

interface InvoiceTypeOption {
  name?: string
  label?: string
  value: string
}

interface Beneficiary {
  beneficiary: {
    type?: string
    value: {
      name?: string
      partyId?: string
      partyTag?: string
      reference?: string
      amount?: number | string
      vat?: boolean
    }
  }
}

interface BeforeContentProps {
  amount: string
  onAmountChange?: (e: any) => void
  invoiceTypes: InvoiceTypeOption[]
  invoiceType?: InvoiceTypeOption
  onInvoiceTypeChange?: (data: any) => void
  errors: Record<string, any>
  vatApplied?: boolean
  onVatChange?: (vatApplied: any) => void
  description?: string
  onDescriptionChange?: (e: any) => void
  portfolioId?: string
  customerId?: string
  customerTag?: string
}

interface PaymentAllocationProps {
  invoiceType?: InvoiceTypeOption
  invoiceTypes?: InvoiceTypeOption[]
  onInvoiceTypeChange?: (data: any) => void
  amount: string
  onAmountChange?: (e: any) => void
  vatApplied?: boolean
  onVatChange?: (vatApplied: any) => void
  onBeneficiaryVatChange?: (index: any, vatApplied: any) => void
  onNewBeneficiaryAdded?: (beneficiary: any, index: number) => void
  onBeneficiaryRemoved?: (index: number) => void
  onNewBeneficiaryAmountChange?: (index: number, e: any) => void
  onBeneficiaryReferenceChange?: (index: number, reference: any, type: any) => void
  onBeneficiariesOrderChange?: (...args: any[]) => void
  beneficiaries?: Beneficiary[]
  onDepositBeneficiaryTransferChange?: (index: number, transfer: any) => void
  propertyOwnerName?: string
  portfolioId?: string
  customerId?: string
  customerTag?: string
  partyTags?: Array<{ value: string; label: string }>
  errors?: Record<string, any>
  easyPayData?: any
  fetchEasyPayData?: (...args: any[]) => void
  description?: string
  onDescriptionChange?: (e: any) => void
}

const BeforeContent = ({
  amount,
  onAmountChange,
  invoiceTypes,
  invoiceType,
  onInvoiceTypeChange,
  errors,
  vatApplied,
  onVatChange,
  description,
  onDescriptionChange,
  portfolioId,
  customerId,
  customerTag,
}: BeforeContentProps): React.ReactElement => {
  const _invoiceTypes =
    portfolioId && customerTag === 'Application'
      ? invoiceTypes.filter((type: $TSFixMe) => type.value === 'ApplicationFee' || type.value === 'TenantRefund')
      : portfolioId
      ? invoiceTypes
      : invoiceTypes.filter((type: $TSFixMe) => !depositInvoiceTypes.includes(type.value))

  return (
    <Segment>
      <Segment horizontal>
        <div className={styles['invoice-type']}>
          <Select
            isSearchable
            name="invoiceType"
            value={_invoiceTypes.find((type: any) => type.value === invoiceType?.value)}
            label="Invoice Type"
            options={_invoiceTypes}
            onChange={onInvoiceTypeChange}
            error={errors.invoiceType}
            isDisabled={!customerId}
          />
        </div>
        <div className={styles.amount}>
          <CurrencyField
            includeVatControl
            label={`Amount ${vatApplied ? '(incl. VAT)' : ''}`}
            name="amount"
            placeholder="Enter amount"
            onChange={onAmountChange}
            value={amount}
            vatApplied={vatApplied}
            isEditVatDisabled={depositInvoiceTypes.includes(invoiceType?.value ?? '')}
            onToggleChange={onVatChange}
            error={errors.amount}
          />
        </div>
      </Segment>
      <Segment horizontal>
        <TextField
          inputComponent={
            <TextInput
              type={TextFieldTypes.text}
              name="clickupRef"
              value={description}
              onChange={onDescriptionChange}
            />
          }
          label="Description"
        />
      </Segment>
    </Segment>
  )
}

const PaymentAllocation = (props: PaymentAllocationProps): React.ReactElement => {
  const {
    invoiceType,
    invoiceTypes = [],
    onInvoiceTypeChange,
    amount,
    onAmountChange = (e: any) => e,
    beneficiaries = [],
    onNewBeneficiaryAdded = (beneficiary: any) => beneficiary,
    onBeneficiaryRemoved,
    onNewBeneficiaryAmountChange,
    onBeneficiariesOrderChange,
    partyTags,
    errors = {},
    vatApplied,
    onVatChange,
    onBeneficiaryVatChange,
    easyPayData,
    fetchEasyPayData,
    onDepositBeneficiaryTransferChange,
    description,
    onDescriptionChange,
    onBeneficiaryReferenceChange,
    portfolioId,
    customerId,
    customerTag,
  } = props

  const propertyOwnerId = useSelector((state: any) =>
    portfolioApiSelectors.getPrimaryLandlordByPortfolioId(state)(portfolioId),
  )
  const propertyOwnerName = useSelector((state: any) => partyApiSelectors.getPartyNameById(state)(propertyOwnerId))
  const propertyOwnerBankDetails = useSelector((state: any) =>
    partyApiSelectors.getPartyBankDetails(state)(propertyOwnerId),
  )

  const getPaymentRuleAmountsSum = (): $TSFixMe =>
    pipe(map(pathOr(0, ['beneficiary', 'value', 'amount'])), sum)(beneficiaries)

  const getUnallocatedAmount = (): number => {
    const invoiceAmount = parseFloat(amount)
    return invoiceAmount ? invoiceAmount - getPaymentRuleAmountsSum() : 0
  }

  const buildFundDistributionFooter = () => {
    const invoiceAmount = parseFloat(amount)
    return (
      <FundDistributionFooter
        text={'Amount Unallocated'}
        amount={getUnallocatedAmount()}
        isComplete={getUnallocatedAmount() === 0}
        progress={{
          value: 100 - ((invoiceAmount - getPaymentRuleAmountsSum()) / invoiceAmount) * 100,
          style: 'default',
        }}
      />
    )
  }

  return (
    <div className={styles.root}>
      <FundDistribution
        headerText="Payment Allocation"
        beforeContent={
          <BeforeContent
            amount={amount}
            invoiceType={invoiceType}
            invoiceTypes={invoiceTypes}
            onAmountChange={onAmountChange}
            onInvoiceTypeChange={onInvoiceTypeChange}
            errors={errors}
            onVatChange={onVatChange}
            vatApplied={vatApplied}
            description={description}
            onDescriptionChange={onDescriptionChange}
            portfolioId={portfolioId}
            customerId={customerId}
            customerTag={customerTag}
          />
        }
        balance={parseFloat(amount)}
        onNewBeneficiaryAdded={onNewBeneficiaryAdded}
        onBeneficiaryRemoved={onBeneficiaryRemoved}
        onNewBeneficiaryAmountChange={onNewBeneficiaryAmountChange}
        onBeneficiariesOrderChange={onBeneficiariesOrderChange}
        onBeneficiaryReferenceChange={onBeneficiaryReferenceChange}
        beneficiaries={beneficiaries}
        partySearchTags=""
        partyTags={partyTags}
        errors={errors}
        easyPayData={easyPayData}
        onVatChange={onBeneficiaryVatChange}
        Footer={buildFundDistributionFooter}
        fetchEasyPayData={fetchEasyPayData}
        invoiceType={{ name: invoiceType?.name ?? null, value: invoiceType?.value ?? null }}
        onDepositBeneficiaryTransferChange={onDepositBeneficiaryTransferChange}
        propertyOwnerName={propertyOwnerName}
        propertyOwnerId={propertyOwnerId}
        propertyOwnerBankDetails={propertyOwnerBankDetails}
        disableDepositEdit={true}
        unallocatedAmount={parseFloat(amount) - getPaymentRuleAmountsSum() || 0}
        portfolioId={portfolioId}
      />
    </div>
  )
}

export default subscribe([AdhocInvoiceProvider], ({ fetchEasyPayData, easyPayData }: any) => ({
  fetchEasyPayData,
  easyPayData,
}))(PaymentAllocation)
